import type { AgentAdapter, DiscoveredFile, ParseBatch } from "@tktuner/core";
import { detectClaudeRoot, listLogFiles } from "./discover";
import { iterateJsonlLines } from "./jsonl";
import { parseLine } from "./parse";

const BATCH_LINES = 2000;

export class ClaudeCodeAdapter implements AgentAdapter {
  readonly kind = "claude-code" as const;

  detect(rootOverride?: string): { found: boolean; rootDirs: string[] } {
    return detectClaudeRoot(rootOverride);
  }

  listLogFiles(rootDirs: string[]): DiscoveredFile[] {
    return listLogFiles(rootDirs);
  }

  async *parseFile(file: DiscoveredFile, fromOffset: number): AsyncIterable<ParseBatch> {
    let batch = emptyBatch(fromOffset);
    for await (const line of iterateJsonlLines(file.path, fromOffset)) {
      const outcome = parseLine(line.text, file);
      batch.linesParsed++;
      if (line.terminated) {
        batch.bytesConsumed = line.endOffset;
        if (outcome.turn) batch.turns.push(outcome.turn);
        if (outcome.event) batch.events.push(outcome.event);
        if (outcome.unknownType) {
          batch.unknownTypes[outcome.unknownType] =
            (batch.unknownTypes[outcome.unknownType] ?? 0) + 1;
        }
        if (outcome.parseError) batch.parseErrors++;
      } else {
        // Trailing line with no newline: usually a live session mid-write.
        // Ingest a complete turn if one parsed (uuid PK makes the re-read on
        // the next scan idempotent), but never events (no natural key) and
        // never advance bytesConsumed past the last terminated line. A parse
        // failure here is expected, not an error.
        if (outcome.turn) batch.turns.push(outcome.turn);
      }
      if (batch.linesParsed >= BATCH_LINES) {
        yield batch;
        batch = emptyBatch(batch.bytesConsumed);
      }
    }
    // Always yield the final (possibly empty) batch so the caller can
    // checkpoint file state even when nothing new was read.
    yield batch;
  }
}

function emptyBatch(offset: number): ParseBatch {
  return {
    turns: [],
    events: [],
    bytesConsumed: offset,
    linesParsed: 0,
    parseErrors: 0,
    unknownTypes: {},
  };
}
